import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CHANNEL_LABEL, type AskBlock, type AskResponse } from '@coachrex/shared';
import { useAsk, useMe } from '../lib/api';
import { engagementTone, relativeStamp, scoreColor, toneFg } from '../lib/format';
import { Fox } from '../components/Fox';
import { GradeBadge } from '../components/ui';
import { useToast } from '../components/Toast';
import './askRex.css';

const SUGGESTIONS = [
  'Which A and B leads are waiting on us right now?',
  'Which lead sources book the most consultations this month?',
  'Who handles price objections best?',
  'What are leads saying about financing?',
];

interface Turn {
  question: string;
  answer?: AskResponse;
  error?: string;
}

const KEY = 'coachrex.askRex';

/** Ask Lex (handoff "Ask Lex"): questions about leads, reps and conversations, answered from Coach Lex data. */
export function AskRex() {
  const me = useMe();
  const ask = useAsk();
  const [thread, setThread] = useState<Turn[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as Turn[];
    } catch {
      return [];
    }
  });
  const [draft, setDraft] = useState('');
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(thread.slice(-10)));
    } catch {
      /* storage unavailable */
    }
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread]);

  const send = (question: string) => {
    const q = question.trim();
    if (!q || ask.isPending) return;
    const history = thread.filter((t) => t.answer).map((t) => ({ question: t.question, answer: t.answer!.answer }));
    setThread((t) => [...t, { question: q }]);
    setDraft('');
    ask.mutate(
      { question: q, history },
      {
        onSuccess: (answer) => setThread((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, answer } : x))),
        onError: (e) => setThread((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, error: e.message } : x))),
      },
    );
  };

  const workspace = me.data?.company.name ?? 'your workspace';
  return (
    <div className="page ask-page">
      <div className="page-head">
        <div className="page-head__titles">
          <h1 className="h1">Ask Lex</h1>
          <span className="subtitle">Ask anything about your leads, reps and conversations. Answers link to the real records.</span>
        </div>
        {thread.length > 0 && (
          <button className="btn btn--secondary btn--sm" onClick={() => setThread([])} disabled={ask.isPending}>
            New conversation
          </button>
        )}
      </div>

      {thread.length === 0 && (
        <section className="card stack-10">
          <span className="card-title">Try asking</span>
          {SUGGESTIONS.map((s) => (
            <button key={s} className="ask-suggestion" onClick={() => send(s)}>
              <span style={{ color: 'var(--accent)' }}>→</span> {s}
            </button>
          ))}
        </section>
      )}

      <div className="ask-thread">
        {thread.map((t, i) => (
          <div key={i} className="ask-turn">
            <div className="ask-q">{t.question}</div>
            <div className="ask-a">
              <Fox size={32} />
              <div className="card ask-card">
                {t.answer ? <Answer a={t.answer} onAsk={send} /> : t.error ? <span style={{ color: 'var(--red-fg)', fontSize: 13.5 }}>{t.error}</span> : <span className="ask-pending">Looking through your leads…</span>}
              </div>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>

      <div className="ask-input-wrap">
        <form
          className="ask-input"
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ask about leads, reps or sources…" aria-label="Ask Lex" maxLength={2000} />
          <button className="btn btn--accent" disabled={!draft.trim() || ask.isPending}>
            Ask
          </button>
        </form>
        <span className="card-meta" style={{ textAlign: 'center' }}>
          {me.data?.user.role === 'rep' ? 'Answers use only leads assigned to you.' : `Answers use all leads in ${workspace}.`} Each question uses a few cents of AI.
        </span>
      </div>
    </div>
  );
}

function Answer({ a, onAsk }: { a: AskResponse; onAsk: (q: string) => void }) {
  return (
    <>
      {a.steps.length > 0 && (
        <div className="ask-steps">
          {a.steps.map((s, i) => (
            <span key={i}>
              <b style={{ color: 'var(--green-fg)' }}>✓</b> {s}
            </span>
          ))}
        </div>
      )}
      <div className="ask-text">{a.answer}</div>
      {a.blocks.map((b, i) => (
        <Block key={i} b={b} timeZone={a.timeZone} />
      ))}
      {a.followup && (
        <button className="ask-followup" onClick={() => onAsk(a.followup!)}>
          → {a.followup}
        </button>
      )}
      <span className="card-meta">{a.basis}</span>
    </>
  );
}

function Block({ b, timeZone }: { b: AskBlock; timeZone: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  if (b.kind === 'leads') {
    return (
      <div className="ask-block">
        <span className="ask-block__title">{b.title}</span>
        {b.rows.map((r) => (
          <button key={r.leadId} className="ask-lead" onClick={() => navigate(`/conversations/${r.leadId}`)}>
            <GradeBadge grade={r.grade} size={22} />
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
              <span style={{ fontWeight: 600 }}>{r.name}</span>
              <span className="card-meta">with {r.rep ?? 'nobody'}</span>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
              <span className="ask-clip">{r.latest}</span>
              <span className="card-meta" style={{ color: r.meta.includes('waiting') ? 'var(--red-fg)' : undefined }}>
                {r.meta}
              </span>
            </span>
            <span style={{ fontWeight: 600, color: toneFg(engagementTone(r.engagementLabel)) }}>{r.engagement}</span>
          </button>
        ))}
        <Link to="/conversations" style={{ fontSize: 12.5 }}>
          Open Conversations →
        </Link>
      </div>
    );
  }
  if (b.kind === 'chart') {
    const max = Math.max(...b.rows.map((r) => r.value), 1);
    const pct = b.unit === '%' || b.unit === '/100';
    return (
      <div className="ask-block">
        <span className="ask-block__title">{b.title}</span>
        {b.rows.map((r) => (
          <div key={r.label} className="ask-bar">
            <span className="ask-clip">{r.label}</span>
            <span className="bar">
              <span style={{ width: `${(100 * r.value) / (pct ? 100 : max)}%`, background: pct ? scoreColor(r.value) : 'var(--accent)' }} />
            </span>
            <b>
              {r.value}
              {b.unit === '%' ? '%' : b.unit === 'min' ? ' min' : ''}
            </b>
            <span className="card-meta">n={r.n}</span>
          </div>
        ))}
      </div>
    );
  }
  if (b.kind === 'quotes') {
    return (
      <div className="ask-block">
        <span className="ask-block__title">{b.title}</span>
        {b.rows.map((q) => (
          <button key={q.touchId} className="ask-quote" onClick={() => navigate(`/conversations/${q.leadId}`)}>
            <span className="card-meta">
              <span className="chan-chip">{CHANNEL_LABEL[q.channel]}</span> {relativeStamp(q.at, timeZone)} · {q.who}
              {q.who !== q.leadName ? ` → ${q.leadName}` : ''}
            </span>
            <span>“{q.text}”</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="ask-block ask-draft">
      <span className="ask-block__title">{b.title}</span>
      <span style={{ whiteSpace: 'pre-wrap' }}>{b.text}</span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn--secondary btn--sm"
          onClick={() => {
            void navigator.clipboard?.writeText(b.text);
            toast({ text: 'Copied. Paste it into the conversation in your CRM.' });
          }}
        >
          Copy text
        </button>
        {b.crmUrl && (
          <a className="btn btn--secondary btn--sm" href={b.crmUrl} target="_blank" rel="noreferrer">
            Open in CRM
          </a>
        )}
      </span>
    </div>
  );
}
